import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
} from '@mui/material';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ScheduleIcon from '@mui/icons-material/Schedule';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import StepDetailDrawer from './StepDetailDrawer';
import type { DispatchStep, DispatchLogEvent } from '../../types/dispatch';

interface StepsTimelineProps {
  steps: DispatchStep[];
  runId: string;
  logs: DispatchLogEvent[];
  fetchStepLogs?: (runId: string, stepId: number) => Promise<unknown[]>;
}

const STATUS_ICONS: Record<string, React.ReactNode> = {
  succeeded: <CheckCircleIcon color="success" />,
  success: <CheckCircleIcon color="success" />,
  failed: <CancelIcon color="error" />,
  partial: <WarningAmberIcon color="warning" />,
  running: <ScheduleIcon color="info" />,
};

const StepsTimeline: React.FC<StepsTimelineProps> = ({
  steps,
  runId,
  logs,
  fetchStepLogs,
}) => {
  const [drawerStep, setDrawerStep] = useState<DispatchStep | null>(null);

  const stepLogs = drawerStep
    ? logs.filter((l) => l.step_id === drawerStep.step_id)
    : [];

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Steps Timeline
      </Typography>
      <List dense>
        {steps.map((step) => (
          <ListItem key={step.step_id} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              onClick={() => setDrawerStep(step)}
              sx={{
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor:
                  step.status === 'failed' ? 'error.50' : 'background.paper',
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                {STATUS_ICONS[step.status] || (
                  <CheckCircleIcon color="disabled" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" fontWeight={500}>
                      {step.step_name}
                    </Typography>
                    <Chip
                      label={step.duration_ms != null ? `${step.duration_ms}ms` : '—'}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                }
                secondary={step.status}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <StepDetailDrawer
        open={!!drawerStep}
        step={drawerStep}
        stepLogs={stepLogs}
        runId={runId}
        onClose={() => setDrawerStep(null)}
        fetchStepLogs={fetchStepLogs}
      />
    </Box>
  );
};

export default StepsTimeline;
